/**
 * Resources Service
 *
 * API endpoints for investment resource operations
 */

import { apiClient } from '../client';

import type { ApiResponse } from '../types';
import type { InvestmentResource, ResourceType } from '@medsuite/models';

export interface CreateResourceDto {
  investment_id: number;
  resource_type: ResourceType;
  resource_name: string;
  project_role?: string;
  resource_start_date?: Date | string;
  resource_end_date?: Date | string;
  resource_allocation?: number;
  quantifier?: string;
  temporary_or_perpetual?: string;
}

export interface UpdateResourceDto extends Partial<Omit<CreateResourceDto, 'investment_id'>> {}

/**
 * Resources Service
 */
class ResourcesService {
  /**
   * Get all resources for an investment
   */
  async getAll(investmentId: number): Promise<ApiResponse<InvestmentResource[]>> {
    return apiClient.get<InvestmentResource[]>(`/investments/${investmentId}/resources`);
  }

  /**
   * Get single resource by ID
   */
  async getById(
    investmentId: number,
    resourceId: number
  ): Promise<ApiResponse<InvestmentResource>> {
    return apiClient.get<InvestmentResource>(
      `/investments/${investmentId}/resources/${resourceId}`
    );
  }

  /**
   * Create new resource
   */
  async create(
    investmentId: number,
    data: Omit<CreateResourceDto, 'investment_id'>
  ): Promise<ApiResponse<InvestmentResource>> {
    return apiClient.post<InvestmentResource>(`/investments/${investmentId}/resources`, {
      ...data,
      investment_id: investmentId,
    });
  }

  /**
   * Update resource
   */
  async update(
    investmentId: number,
    resourceId: number,
    data: UpdateResourceDto
  ): Promise<ApiResponse<InvestmentResource>> {
    return apiClient.put<InvestmentResource>(
      `/investments/${investmentId}/resources/${resourceId}`,
      data
    );
  }

  /**
   * Delete resource
   */
  async delete(investmentId: number, resourceId: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/investments/${investmentId}/resources/${resourceId}`);
  }
}

export const resourcesService = new ResourcesService();
